import React from 'react'
import styled from '@emotion/styled'
import { Button } from '@mobi/component-library/Base/Buttons'
import { MoneyInput } from './MoneyInput'

type ShortcutAmount = { amount: number; label: string }
const defaultShortcutAmounts: ShortcutAmount[] = [
  { amount: 5, label: '$5' },
  { amount: 10, label: '$10' },
  { amount: 20, label: '$20' },
  { amount: 50, label: '$50' },
  { amount: 100, label: '$100' },
]

export type PaymentAmountProps = {
  helpText: string | React.ReactNode
  isDisabled: boolean
  inputValue: string
  shortcutAmounts?: ShortcutAmount[]
  onAmountChange: (value: string) => void
  maxAmount?: number
}

export const fiveDigitMoneyInputRegex = /^(\d{0,5}(\.\d{0,2})?)?.*/
export function sanitizeInput(input: string, regex: RegExp) {
  return input.replace(regex, '$1')
}

export const PaymentAmount = ({
  helpText,
  isDisabled,
  inputValue,
  shortcutAmounts = defaultShortcutAmounts,
  onAmountChange,
  maxAmount,
}: PaymentAmountProps) => {
  const onInputValueChanged = (inputValue: string) => {
    const sanitizedValue = sanitizeInput(inputValue, fiveDigitMoneyInputRegex)
    onAmountChange(sanitizedValue)
  }

  const onAddDefinedAmount = (amount: number) => {
    const currentAmount = (+inputValue || 0) * 100
    const amountToAdd = amount * 100
    const newAmount = (currentAmount + amountToAdd) / 100
    onAmountChange(newAmount.toFixed(2))
  }

  const isAmountExceedingMaxAmount = (amount: number) => {
    if (maxAmount === undefined) return false

    return +inputValue + amount > maxAmount
  }

  // Tack on cents if not entered by user
  const onBlur = (inputValue: string) => {
    if (inputValue.length > 0 && !inputValue.includes('.')) {
      onAmountChange(inputValue.concat('.00'))
    }
  }

  return (
    <PaymentAmountContainerStyled>
      <InputContainerStyled>
        <MoneyInput
          id='amount-input'
          label=''
          name='amount-input'
          inputValue={inputValue}
          helpText={helpText}
          onClearButtonPress={() => onAmountChange('')}
          onInputValueChange={e => onInputValueChanged(e.currentTarget.value)}
          onBlur={e => onBlur(e.target.value)}
          isDisabled={isDisabled}
        />
      </InputContainerStyled>

      <AmountButtonsContainerStyled>
        {shortcutAmounts.map(({ amount, label }) => (
          <AmountButtonStyled
            key={label}
            color='secondary_outline'
            onClick={() => onAddDefinedAmount(amount)}
            disabled={isDisabled || isAmountExceedingMaxAmount(amount)}
          >
            {label}
          </AmountButtonStyled>
        ))}
      </AmountButtonsContainerStyled>
    </PaymentAmountContainerStyled>
  )
}

const PaymentAmountContainerStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '3rem',
})

const InputContainerStyled = styled.div({
  width: '32rem',
})

const AmountButtonsContainerStyled = styled.div({
  width: '100%',
  display: 'flex',
  gap: '1rem',
  margin: 'auto',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  alignItems: 'stretch',
})

const AmountButtonStyled = styled(Button)({
  width: '0',
  flex: '1 1 0px',
})
